import React, { useEffect, useState } from 'react'
import moment from 'moment'
import styled, { keyframes } from 'styled-components'
import { useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useApi } from '../../hooks/useApi'
import Calendar from '../Calendar'

interface ProgrammedReport {
  _id: string
  mimicId: string
  hour: number
  mode: 'weekly' | 'biweekly' | 'first-of-month' | 'last-of-month' | 'on-day' | 'on-day-from-report'
  startDate: string
  type: 'general-report' | 'general-report-assets-balance'
  includeSolana: boolean
  includeRainbowSummary: boolean
  includeBalancerSummary: boolean
  chainId?: number
  createdAt: string
  updatedAt: string
  nextExecutionDate?: string
  fromReport?: string
  fromDay?: number
  fromHour?: number
  toDay?: number
  toHour?: number
}

const DATE_FORMAT = 'MMM DD, YYYY HH:mm'

const modeLabels: Record<string, string> = {
  weekly: 'Weekly',
  biweekly: 'Bi-weekly',
  'first-of-month': 'First of month',
  'last-of-month': 'Last of month',
  'on-day': 'On day',
  'on-day-from-report': 'On day from report',
}

const typeLabels: Record<string, string> = {
  'general-report': 'General',
  'general-report-assets-balance': 'General + Assets',
}

const emptyForm = {
  hour: 0,
  mode: 'weekly' as ProgrammedReport['mode'],
  startDate: new Date(),
  type: 'general-report' as ProgrammedReport['type'],
  includeSolana: false,
  includeRainbowSummary: false,
  includeBalancerSummary: false,
  chainId: undefined as number | undefined,
  fromReport: '',
  fromDay: undefined as number | undefined,
  fromHour: undefined as number | undefined,
  toDay: undefined as number | undefined,
  toHour: undefined as number | undefined,
}

const AutoReports: React.FC = () => {
  const { callApi } = useApi()
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [data, setReports] = useState<ProgrammedReport[] | null>(null)
  const [filteredData, setFilteredData] = useState<ProgrammedReport[] | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingReport, setEditingReport] = useState<ProgrammedReport | null>(null)
  const [showAllReports, setShowAllReports] = useState(false)
  const [formData, setFormData] = useState(emptyForm)
  const params = useParams()

  const fetchReports = async () => {
    try {
      const endpoint = showAllReports ? '/reports/programmed-reports' : `/reports/programmed-reports/${params.id}`
      const reports = await callApi<ProgrammedReport[]>(endpoint)
      setReports(reports)
      setFilteredData(reports)
    } catch (error) {
      toast.error('Failed to fetch programmed reports.')
    }
  }

  const refetchReports = async () => {
    setIsRefreshing(true)
    try {
      const endpoint = showAllReports ? '/reports/programmed-reports' : `/reports/programmed-reports/${params.id}`
      const reports = await callApi<ProgrammedReport[]>(endpoint)
      setReports(reports)
      setFilteredData(reports)
      toast.success('Programmed reports updated!')
    } catch (error) {
      toast.error('Failed to fetch programmed reports.')
    } finally {
      setIsRefreshing(false)
    }
  }

  useEffect(() => { fetchReports() }, [showAllReports])

  const buildPayload = (mimicId: string) => {
    const adjustedStart = moment.utc(formData.startDate).set({ hour: formData.hour, minute: 0, second: 0, millisecond: 0 }).toISOString()
    return {
      mimicId,
      hour: formData.hour,
      mode: formData.mode,
      startDate: adjustedStart,
      type: formData.type,
      includeSolana: formData.includeSolana,
      includeRainbowSummary: formData.includeRainbowSummary,
      includeBalancerSummary: formData.includeBalancerSummary,
      ...(formData.chainId ? { chainId: formData.chainId } : {}),
      ...(formData.fromReport ? { fromReport: formData.fromReport } : {}),
      ...(formData.fromDay !== undefined ? { fromDay: formData.fromDay } : {}),
      ...(formData.fromHour !== undefined ? { fromHour: formData.fromHour } : {}),
      ...(formData.toDay !== undefined ? { toDay: formData.toDay } : {}),
      ...(formData.toHour !== undefined ? { toHour: formData.toHour } : {}),
    }
  }

  const handleCreate = async () => {
    if (!params.id) { toast.error('No mimic ID found'); return }
    try {
      await callApi('/reports/programmed-reports', buildPayload(params.id), 'POST')
      toast.success('Programmed report created!')
      setShowCreateForm(false)
      setFormData(emptyForm)
      fetchReports()
    } catch (error) {
      toast.error('Failed to create programmed report.')
    }
  }

  const handleUpdate = async () => {
    if (!editingReport) return
    try {
      await callApi(`/reports/programmed-reports/${editingReport._id}`, buildPayload(editingReport.mimicId), 'PUT')
      toast.success('Programmed report updated!')
      setEditingReport(null)
      setFormData(emptyForm)
      fetchReports()
    } catch (error) {
      toast.error('Failed to update programmed report.')
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this programmed report?')) return
    try {
      await callApi(`/reports/programmed-reports/${id}`, {}, 'DELETE')
      toast.success('Deleted successfully!')
      fetchReports()
    } catch (error) {
      toast.error('Failed to delete programmed report.')
    }
  }

  const handleEdit = (report: ProgrammedReport) => {
    setEditingReport(report)
    setFormData({
      hour: report.hour,
      mode: report.mode,
      startDate: new Date(report.startDate),
      type: report.type,
      includeSolana: report.includeSolana,
      includeRainbowSummary: report.includeRainbowSummary,
      includeBalancerSummary: report.includeBalancerSummary,
      chainId: report.chainId,
      fromReport: report.fromReport || '',
      fromDay: report.fromDay,
      fromHour: report.fromHour,
      toDay: report.toDay,
      toHour: report.toHour,
    })
    setShowCreateForm(false)
  }

  const handleCancel = () => {
    setShowCreateForm(false)
    setEditingReport(null)
    setFormData(emptyForm)
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    toast.success(`${label} copied!`)
  }

  const field = (key: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const val = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value
    setFormData({ ...formData, [key]: val })
  }

  const numField = (key: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFormData({ ...formData, [key]: e.target.value ? parseInt(e.target.value) : undefined })

  const showForm = showCreateForm || !!editingReport

  return (
    <Wrap>


      <Toolbar>
        <PrimaryBtn onClick={() => { setShowCreateForm(!showCreateForm); setEditingReport(null) }}>
          {showCreateForm ? 'Cancel' : '+ New scheduled report'}
        </PrimaryBtn>
        <IconBtn onClick={refetchReports} disabled={isRefreshing} title="Refresh">
          {isRefreshing ? <Spinner /> : <RefreshIcon />}
        </IconBtn>
        <CheckLabel style={{ marginLeft: 8 }}>
          <input type="checkbox" checked={showAllReports} onChange={(e) => setShowAllReports(e.target.checked)} />
          Show all environments
        </CheckLabel>
      </Toolbar>

      {showForm && (
        <FormCard>
          <FormTitle>{editingReport ? 'Edit scheduled report' : 'New scheduled report'}</FormTitle>
          <FormGrid>
            <FormField>
              <FieldLabel>Hour (UTC)</FieldLabel>
              <FieldInput type="number" min="0" max="23" value={formData.hour} onChange={field('hour')} />
            </FormField>
            <FormField>
              <FieldLabel>Mode</FieldLabel>
              <FieldSelect value={formData.mode} onChange={field('mode')}>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-weekly</option>
                <option value="first-of-month">First of month</option>
                <option value="last-of-month">Last of month</option>
                <option value="on-day">On day</option>
                <option value="on-day-from-report">On day from report</option>
              </FieldSelect>
            </FormField>
            <FormField>
              <FieldLabel>Start date</FieldLabel>
              <Calendar selectedDate={formData.startDate} withHour={false} onChange={(d) => setFormData({ ...formData, startDate: d || new Date() })} />
            </FormField>
            <FormField>
              <FieldLabel>Type</FieldLabel>
              <FieldSelect value={formData.type} onChange={field('type')}>
                <option value="general-report">General Report</option>
                <option value="general-report-assets-balance">General + Assets Balance</option>
              </FieldSelect>
            </FormField>
            <FormField>
              <FieldLabel>Chain ID <FieldHint>optional</FieldHint></FieldLabel>
              <FieldInput type="number" value={formData.chainId || ''} onChange={numField('chainId')} placeholder="All chains" />
            </FormField>
            <FormField>
              <FieldLabel>From report <FieldHint>optional</FieldHint></FieldLabel>
              <FieldInput type="text" value={formData.fromReport} onChange={field('fromReport')} placeholder="Report ID" />
            </FormField>
            <FormField>
              <FieldLabel>From day <FieldHint>0–31</FieldHint></FieldLabel>
              <FieldInput type="number" min="0" max="31" value={formData.fromDay || ''} onChange={numField('fromDay')} placeholder="optional" />
            </FormField>
            <FormField>
              <FieldLabel>From hour <FieldHint>0–23</FieldHint></FieldLabel>
              <FieldInput type="number" min="0" max="23" value={formData.fromHour || ''} onChange={numField('fromHour')} placeholder="optional" />
            </FormField>
            <FormField>
              <FieldLabel>To day <FieldHint>0–31</FieldHint></FieldLabel>
              <FieldInput type="number" min="0" max="31" value={formData.toDay || ''} onChange={numField('toDay')} placeholder="optional" />
            </FormField>
            <FormField>
              <FieldLabel>To hour <FieldHint>0–23</FieldHint></FieldLabel>
              <FieldInput type="number" min="0" max="23" value={formData.toHour || ''} onChange={numField('toHour')} placeholder="optional" />
            </FormField>

            <FormField style={{ gridColumn: '1 / -1' }}>
              <CheckRow>
                <CheckLabel><input type="checkbox" checked={formData.includeSolana} onChange={field('includeSolana')} />Include Solana</CheckLabel>
                <CheckLabel><input type="checkbox" checked={formData.includeRainbowSummary} onChange={field('includeRainbowSummary')} />Rainbow summary</CheckLabel>
                <CheckLabel><input type="checkbox" checked={formData.includeBalancerSummary} onChange={field('includeBalancerSummary')} />Balancer summary</CheckLabel>
              </CheckRow>
            </FormField>
          </FormGrid>

          <FormActions>
            <PrimaryBtn onClick={editingReport ? handleUpdate : handleCreate}>
              {editingReport ? 'Update' : 'Create'}
            </PrimaryBtn>
            <GhostBtn onClick={handleCancel}>Cancel</GhostBtn>
          </FormActions>
        </FormCard>
      )}

      {filteredData === null ? (
        <LoaderBar />
      ) : (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th>Mimic ID</Th>
                <Th>ID</Th>
                <Th>Hour</Th>
                <Th>Mode</Th>
                <Th>Start date</Th>
                <Th>Type</Th>
                <Th>Chain</Th>
                <Th>Solana</Th>
                <Th>Rainbow</Th>
                <Th>Balancer</Th>
                <Th>Created at</Th>
                <Th>Next run</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {filteredData
                .slice()
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .map((item, index) => (
                  <tr key={index}>
                    <Td>
                      <MonoId>
                        {item.mimicId.substring(0, 8)}…
                        <CopyBtn onClick={() => copyToClipboard(item.mimicId, 'Mimic ID')} title="Copy">
                          <CopyIcon />
                        </CopyBtn>
                      </MonoId>
                    </Td>
                    <Td>
                      <MonoId>
                        {item._id.substring(0, 8)}…
                        <CopyBtn onClick={() => copyToClipboard(item._id, 'ID')} title="Copy">
                          <CopyIcon />
                        </CopyBtn>
                      </MonoId>
                    </Td>
                    <Td>{item.hour}:00</Td>
                    <Td><ModeBadge>{modeLabels[item.mode] || item.mode}</ModeBadge></Td>
                    <Td>{moment.utc(item.startDate).format('DD-MM-YYYY HH:mm')}</Td>
                    <Td>{typeLabels[item.type] || item.type}</Td>
                    <Td>{item.chainId || <Muted>All</Muted>}</Td>
                    <Td><BoolBadge on={item.includeSolana}>{item.includeSolana ? 'Yes' : 'No'}</BoolBadge></Td>
                    <Td><BoolBadge on={item.includeRainbowSummary}>{item.includeRainbowSummary ? 'Yes' : 'No'}</BoolBadge></Td>
                    <Td><BoolBadge on={item.includeBalancerSummary}>{item.includeBalancerSummary ? 'Yes' : 'No'}</BoolBadge></Td>
                    <Td>{moment.utc(item.createdAt).format(DATE_FORMAT)}</Td>
                    <Td>
                      {item.nextExecutionDate
                        ? <NextRun title={moment.utc(item.nextExecutionDate).format(DATE_FORMAT)}>{moment.utc(item.nextExecutionDate).fromNow()}</NextRun>
                        : <Muted>N/A</Muted>}
                    </Td>
                    <Td>
                      <BtnGroup>
                        <ActionBtn onClick={() => handleEdit(item)}>Edit</ActionBtn>
                        <DangerBtn onClick={() => handleDelete(item._id)}>Delete</DangerBtn>
                      </BtnGroup>
                    </Td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </Wrap>
  )
}

export default AutoReports

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const PrimaryBtn = styled.button`
  height: 34px;
  padding: 0 16px;
  background: rgba(111, 92, 230, 0.15);
  border: 1px solid rgba(111, 92, 230, 0.35);
  border-radius: 6px;
  color: #a89aff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover { background: rgba(111, 92, 230, 0.25); border-color: rgba(111, 92, 230, 0.5); }
`

const GhostBtn = styled.button`
  height: 34px;
  padding: 0 14px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 6px;
  color: #777;
  font-size: 13px;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  &:hover { border-color: rgba(255,255,255,0.15); color: #a0a0a0; }
`

const ActionBtn = styled.button`
  height: 26px;
  padding: 0 9px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 4px;
  color: #a0a0a0;
  font-size: 11px;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  &:hover { border-color: rgba(111,92,230,0.4); color: #a89aff; }
`

const DangerBtn = styled(ActionBtn)`
  &:hover { border-color: rgba(255,68,68,0.4); color: #ff4444; }
`

const IconBtn = styled.button`
  width: 34px; height: 34px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 6px;
  color: #777;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  &:hover:not(:disabled) { border-color: rgba(255,255,255,0.15); color: #a0a0a0; }
  &:disabled { opacity: 0.4; cursor: default; }
`

const spin = keyframes`to { transform: rotate(360deg); }`
const Spinner = styled.div`
  width: 14px; height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(111,92,230,0.2);
  border-top-color: #6f5ce6;
  animation: ${spin} 0.7s linear infinite;
`

const CheckLabel = styled.label`
  display: flex; align-items: center; gap: 6px;
  color: #777; font-size: 12px; cursor: pointer;
  transition: color 0.15s;
  input[type='checkbox'] { accent-color: #6f5ce6; width: 13px; height: 13px; cursor: pointer; }
  &:hover { color: #a0a0a0; }
`

const CheckRow = styled.div`
  display: flex; gap: 16px; flex-wrap: wrap; align-items: center;
`

// ─── Form ────────────────────────────────────────────────────────────────────

const FormCard = styled.div`
  background: #111;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
  padding: 20px;
`

const FormTitle = styled.h3`
  color: #ededed;
  font-size: 13px;
  font-weight: 600;
  margin: 0 0 16px;
`

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FieldLabel = styled.label`
  color: #777;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  gap: 6px;
`

const FieldHint = styled.span`
  color: #777;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
`

const fieldBase = `
  background: #0d0d0d;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 6px;
  color: #ededed;
  font-size: 13px;
  padding: 0 10px;
  height: 32px;
  outline: none;
  transition: border-color 0.15s;
  &::placeholder { color: #777; }
  &:focus { border-color: rgba(111,92,230,0.5); }
`

const FieldInput = styled.input`${fieldBase}`
const FieldSelect = styled.select`
  ${fieldBase}
  cursor: pointer;
  option { background: #161616; color: #ededed; }
`

const FormActions = styled.div`
  display: flex; gap: 8px;
`

// ─── Table ───────────────────────────────────────────────────────────────────

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`

const LoaderBar = styled.div`
  height: 2px; background: rgba(255,255,255,0.06); border-radius: 2px; overflow: hidden; position: relative;
  &::after {
    content: ''; display: block; position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(111,92,230,0.5), transparent);
    animation: ${shimmer} 1.4s ease infinite;
  }
`

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
`

const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 13px;
`

const Th = styled.th`
  padding: 10px 12px; text-align: left; color: #a1a1a1;
  font-size: 11px; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.06em; border-bottom: 1px solid rgba(255,255,255,0.06);
`

const Td = styled.td`
  padding: 8px 12px; color: #a0a0a0; font-size: 12px;
  vertical-align: middle; border-bottom: 1px solid rgba(255,255,255,0.04);
  tr:last-child & { border-bottom: none; }
`

const MonoId = styled.div`
  display: flex; align-items: center; gap: 6px;
  font-family: 'SF Mono', 'Fira Code', monospace; font-size: 11px; color: #777;
`

const CopyBtn = styled.button`
  background: transparent; border: 0; padding: 0;
  color: #777; cursor: pointer; display: inline-flex;
  transition: color 0.15s;
  &:hover { color: #a0a0a0; }
`

const ModeBadge = styled.span`
  display: inline-flex; align-items: center;
  padding: 2px 7px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 4px;
  font-size: 11px; color: #a0a0a0;
`

const BoolBadge = styled.span<{ on: boolean }>`
  color: ${({ on }) => on ? '#33C2B0' : '#333'};
  font-size: 11px;
`

const Muted = styled.span`
  color: #777;
`

const NextRun = styled.span`
  color: #a89aff;
  font-size: 12px;
  cursor: help;
  text-decoration: underline dotted;
  text-underline-offset: 3px;
`

const BtnGroup = styled.div`
  display: flex; gap: 4px;
`

const CopyIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
)

const RefreshIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"></polyline>
    <polyline points="1 20 1 14 7 14"></polyline>
    <path d="m3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
  </svg>
)
